import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Moon, Sun, Brain, Heart, Smile, Frown, Meh,
  Clock, Calendar, TrendingUp, Play, Check, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { wellbeingApi } from '../services/api';
import { toast } from '../hooks/use-toast';

const WellbeingPage = () => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [meditations, setMeditations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sleepForm, setSleepForm] = useState({
    date: new Date().toISOString().split('T')[0],
    sleep_start: '23:00',
    sleep_end: '07:00',
    quality: 'good'
  });
  const [moodSelected, setMoodSelected] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashData, medData] = await Promise.all([
        wellbeingApi.getDashboard(),
        wellbeingApi.getMeditations()
      ]);
      setDashboard(dashData);
      setMeditations(medData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogSleep = async () => {
    try {
      await wellbeingApi.logSleep(sleepForm);
      toast({
        title: language === 'en' ? 'Sleep Logged!' : '¡Sueño Registrado!',
        description: language === 'en' ? 'Your sleep has been recorded' : 'Tu sueño ha sido registrado'
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleLogMood = async (mood) => {
    try {
      setMoodSelected(mood);
      await wellbeingApi.logMood(mood);
      toast({
        title: language === 'en' ? 'Mood Logged!' : '¡Estado de Ánimo Registrado!',
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const tabs = [
    { id: 'overview', label: language === 'en' ? 'Overview' : 'Resumen', icon: Heart },
    { id: 'sleep', label: language === 'en' ? 'Sleep' : 'Sueño', icon: Moon },
    { id: 'meditation', label: language === 'en' ? 'Meditation' : 'Meditación', icon: Brain },
    { id: 'mood', label: language === 'en' ? 'Mood' : 'Ánimo', icon: Smile }
  ];

  const moodOptions = [
    { value: 'great', emoji: '😊', label: language === 'en' ? 'Great' : 'Genial', color: 'emerald' },
    { value: 'good', emoji: '🙂', label: language === 'en' ? 'Good' : 'Bien', color: 'green' },
    { value: 'okay', emoji: '😐', label: language === 'en' ? 'Okay' : 'Normal', color: 'yellow' },
    { value: 'bad', emoji: '😔', label: language === 'en' ? 'Bad' : 'Mal', color: 'orange' },
    { value: 'terrible', emoji: '😢', label: language === 'en' ? 'Terrible' : 'Terrible', color: 'red' }
  ];

  const qualityOptions = [
    { value: 'excellent', label: language === 'en' ? 'Excellent' : 'Excelente' },
    { value: 'good', label: language === 'en' ? 'Good' : 'Bueno' },
    { value: 'fair', label: language === 'en' ? 'Fair' : 'Regular' },
    { value: 'poor', label: language === 'en' ? 'Poor' : 'Malo' }
  ];

  const categoryLabels = {
    breathing: language === 'en' ? 'Breathing' : 'Respiración',
    relaxation: language === 'en' ? 'Relaxation' : 'Relajación',
    focus: language === 'en' ? 'Focus' : 'Enfoque',
    sleep: language === 'en' ? 'Sleep' : 'Sueño',
    gratitude: language === 'en' ? 'Gratitude' : 'Gratitud',
    body_scan: language === 'en' ? 'Body Scan' : 'Escaneo Corporal'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1f1f] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1f1f]">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur-xl border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-6 h-6" />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-bold text-white">
                {language === 'en' ? 'Wellbeing' : 'Bienestar'}
              </h1>
              <p className="text-sm text-gray-400">
                {language === 'en' ? 'Mind, body & soul' : 'Mente, cuerpo y alma'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="text-sm font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-4">
              <StatCard
                icon={Moon}
                value={`${dashboard?.sleep?.avg_hours || 0}h`}
                label={language === 'en' ? 'Avg Sleep' : 'Sueño Prom.'}
                color="indigo"
              />
              <StatCard
                icon={Brain}
                value={dashboard?.meditation?.this_week || 0}
                label={language === 'en' ? 'Meditations' : 'Meditaciones'}
                color="purple"
              />
              <StatCard
                icon={Smile}
                value={dashboard?.mood?.avg_score?.toFixed(1) || '-'}
                label={language === 'en' ? 'Mood Score' : 'Ánimo'}
                color="pink"
              />
            </div>

            {/* Quick Actions */}
            <div className="bg-gray-900/50 rounded-3xl p-6 border border-gray-800">
              <h2 className="text-lg font-bold text-white mb-4">
                {language === 'en' ? 'Quick Actions' : 'Acciones Rápidas'}
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setActiveTab('mood')}
                  className="bg-gray-800/50 p-4 rounded-xl text-left hover:bg-gray-800 transition-colors"
                >
                  <Smile className="w-8 h-8 text-yellow-400 mb-2" />
                  <p className="text-white font-medium">
                    {language === 'en' ? 'Log Mood' : 'Registrar Ánimo'}
                  </p>
                  <p className="text-gray-500 text-sm">
                    {language === 'en' ? 'How are you feeling?' : '¿Cómo te sientes?'}
                  </p>
                </button>
                <button
                  onClick={() => setActiveTab('sleep')}
                  className="bg-gray-800/50 p-4 rounded-xl text-left hover:bg-gray-800 transition-colors"
                >
                  <Moon className="w-8 h-8 text-indigo-400 mb-2" />
                  <p className="text-white font-medium">
                    {language === 'en' ? 'Log Sleep' : 'Registrar Sueño'}
                  </p>
                  <p className="text-gray-500 text-sm">
                    {language === 'en' ? 'Track your rest' : 'Registra tu descanso'}
                  </p>
                </button>
              </div>
            </div>

            {/* Featured Meditations */}
            <div className="bg-gray-900/50 rounded-3xl p-6 border border-gray-800">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">
                  {language === 'en' ? 'Featured Meditations' : 'Meditaciones Destacadas'}
                </h2>
                <button
                  onClick={() => setActiveTab('meditation')}
                  className="text-purple-400 text-sm hover:text-purple-300"
                >
                  {language === 'en' ? 'View All' : 'Ver Todas'}
                </button>
              </div>
              <div className="space-y-3">
                {meditations.filter(m => m.is_featured).slice(0, 3).map((med) => (
                  <Link
                    key={med.id}
                    to={`/wellbeing/meditation/${med.id}`}
                    className="flex items-center gap-4 bg-gray-800/50 p-4 rounded-xl hover:bg-gray-800 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <Brain className="w-6 h-6 text-purple-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-medium">
                        {language === 'en' ? med.title_en : med.title_es}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {med.duration_minutes} min • {categoryLabels[med.category]}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Sleep Tab */}
        {activeTab === 'sleep' && (
          <div className="space-y-6">
            {/* Log Sleep Form */}
            <div className="bg-gray-900/50 rounded-3xl p-6 border border-gray-800">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Moon className="w-5 h-5 text-indigo-400" />
                {language === 'en' ? 'Log Your Sleep' : 'Registra Tu Sueño'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="text-gray-400 text-sm mb-1 block">
                    {language === 'en' ? 'Date' : 'Fecha'}
                  </label>
                  <input
                    type="date"
                    value={sleepForm.date}
                    onChange={(e) => setSleepForm({ ...sleepForm, date: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-400 text-sm mb-1 block">
                      {language === 'en' ? 'Bedtime' : 'Hora de Dormir'}
                    </label>
                    <input
                      type="time"
                      value={sleepForm.sleep_start}
                      onChange={(e) => setSleepForm({ ...sleepForm, sleep_start: e.target.value })}
                      className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-1 block">
                      {language === 'en' ? 'Wake Time' : 'Hora de Despertar'}
                    </label>
                    <input
                      type="time"
                      value={sleepForm.sleep_end}
                      onChange={(e) => setSleepForm({ ...sleepForm, sleep_end: e.target.value })}
                      className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 block">
                    {language === 'en' ? 'Sleep Quality' : 'Calidad del Sueño'}
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {qualityOptions.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setSleepForm({ ...sleepForm, quality: opt.value })}
                        className={`p-3 rounded-xl text-sm font-medium transition-all ${
                          sleepForm.quality === opt.value
                            ? 'bg-indigo-500 text-white'
                            : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={handleLogSleep}
                  className="w-full bg-indigo-500 hover:bg-indigo-600"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'Save Sleep Log' : 'Guardar Registro'}
                </Button>
              </div>
            </div>

            {/* Sleep History */}
            <div className="bg-gray-900/50 rounded-3xl p-6 border border-gray-800">
              <h2 className="text-lg font-bold text-white mb-4">
                {language === 'en' ? 'Sleep History' : 'Historial de Sueño'}
              </h2>
              {dashboard?.sleep?.logs?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.sleep.logs.map((log, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-800/50 p-4 rounded-xl">
                      <div>
                        <p className="text-white font-medium">{log.date}</p>
                        <p className="text-gray-500 text-sm">
                          {log.sleep_start} - {log.sleep_end}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-indigo-400 font-bold">{log.duration_hours}h</p>
                        <p className="text-gray-500 text-sm capitalize">{log.quality}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  {language === 'en' ? 'No sleep logs yet' : 'Aún no hay registros de sueño'}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Meditation Tab */}
        {activeTab === 'meditation' && (
          <div className="space-y-6">
            {/* Meditation Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-900/50 rounded-2xl p-4 border border-gray-800">
                <p className="text-3xl font-bold text-purple-400">
                  {dashboard?.meditation?.total_sessions || 0}
                </p>
                <p className="text-gray-500 text-sm">
                  {language === 'en' ? 'Total Sessions' : 'Sesiones Totales'}
                </p>
              </div>
              <div className="bg-gray-900/50 rounded-2xl p-4 border border-gray-800">
                <p className="text-3xl font-bold text-purple-400">
                  {dashboard?.meditation?.total_minutes || 0}
                </p>
                <p className="text-gray-500 text-sm">
                  {language === 'en' ? 'Minutes Meditated' : 'Minutos Meditados'}
                </p>
              </div>
            </div>

            {/* Meditation List */}
            <div className="bg-gray-900/50 rounded-3xl p-6 border border-gray-800">
              <h2 className="text-lg font-bold text-white mb-4">
                {language === 'en' ? 'All Meditations' : 'Todas las Meditaciones'}
              </h2>
              <div className="space-y-3">
                {meditations.map((med) => (
                  <Link
                    key={med.id}
                    to={`/wellbeing/meditation/${med.id}`}
                    className="flex items-center gap-4 bg-gray-800/50 p-4 rounded-xl hover:bg-gray-800 transition-colors"
                  >
                    {med.image_url ? (
                      <img 
                        src={med.image_url} 
                        alt={med.title_en}
                        className="w-16 h-16 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-purple-500/20 flex items-center justify-center">
                        <Brain className="w-8 h-8 text-purple-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="text-white font-medium">
                        {language === 'en' ? med.title_en : med.title_es}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {med.duration_minutes} min • {categoryLabels[med.category]} • {med.difficulty}
                      </p>
                    </div>
                    <Play className="w-8 h-8 text-purple-400" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Mood Tab */}
        {activeTab === 'mood' && (
          <div className="space-y-6">
            {/* Log Mood */}
            <div className="bg-gray-900/50 rounded-3xl p-6 border border-gray-800">
              <h2 className="text-lg font-bold text-white mb-4">
                {language === 'en' ? 'How are you feeling?' : '¿Cómo te sientes?'}
              </h2>
              <div className="grid grid-cols-5 gap-3">
                {moodOptions.map((mood) => (
                  <button
                    key={mood.value}
                    onClick={() => handleLogMood(mood.value)}
                    className={`p-4 rounded-xl text-center transition-all ${
                      moodSelected === mood.value || dashboard?.mood?.latest?.mood === mood.value
                        ? `bg-${mood.color}-500/30 border-2 border-${mood.color}-500`
                        : 'bg-gray-800/50 hover:bg-gray-800'
                    }`}
                  >
                    <span className="text-3xl">{mood.emoji}</span>
                    <p className="text-gray-400 text-xs mt-1">{mood.label}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Mood History */}
            <div className="bg-gray-900/50 rounded-3xl p-6 border border-gray-800">
              <h2 className="text-lg font-bold text-white mb-4">
                {language === 'en' ? 'Mood History' : 'Historial de Ánimo'}
              </h2>
              {dashboard?.mood?.history?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.mood.history.map((log, idx) => {
                    const mood = moodOptions.find(m => m.value === log.mood);
                    return (
                      <div key={idx} className="flex items-center gap-4 bg-gray-800/50 p-4 rounded-xl">
                        <span className="text-2xl">{mood?.emoji || '😐'}</span>
                        <div className="flex-1">
                          <p className="text-white font-medium">{mood?.label || log.mood}</p>
                          <p className="text-gray-500 text-sm">
                            {new Date(log.logged_at).toLocaleDateString()}
                          </p>
                        </div>
                        {log.note && (
                          <p className="text-gray-400 text-sm max-w-xs truncate">{log.note}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  {language === 'en' ? 'No mood logs yet' : 'Aún no hay registros de ánimo'}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, value, label, color }) => {
  const colors = {
    indigo: 'bg-indigo-500/20 text-indigo-400',
    purple: 'bg-purple-500/20 text-purple-400',
    pink: 'bg-pink-500/20 text-pink-400'
  };

  return (
    <div className="bg-gray-900/50 rounded-2xl p-4 border border-gray-800 text-center">
      <div className={`w-10 h-10 mx-auto rounded-xl ${colors[color]} flex items-center justify-center mb-2`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-gray-500 text-xs">{label}</p>
    </div>
  );
};

export default WellbeingPage;
